/**
 * <GlobalSearch /> — the ⌘K command palette (M21.10).
 *
 * Built on `cmdk`. Opens on ⌘K / Ctrl+K (or by clicking the top-bar
 * search box). Debounces the query, hits `GET /api/search`, and
 * renders results grouped across People / Policies / Transactions /
 * Settings. Natural-language queries surface an "interpreted"
 * result at the top that routes to the relevant filtered page.
 *
 * Recent searches are persisted to localStorage and shown when the
 * query is empty.
 */

import { useQuery } from "@tanstack/react-query";
import { Command } from "cmdk";
import { ArrowRight, Clock, Search, Settings, Sparkles } from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { api } from "../lib/api";
import type { GlobalSearchResponse } from "../lib/api";
import { searchReports } from "../data/reports_registry";
import { searchSettings } from "../data/settings_registry";

const _RECENTS_KEY = "solvait-aihr.recent-searches";
const _MAX_RECENTS = 6;

function loadRecents(): string[] {
  try {
    const raw = localStorage.getItem(_RECENTS_KEY);
    return raw ? (JSON.parse(raw) as string[]) : [];
  } catch {
    return [];
  }
}

function saveRecent(q: string): string[] {
  const trimmed = q.trim();
  if (!trimmed) return loadRecents();
  const next = [
    trimmed,
    ...loadRecents().filter((r) => r !== trimmed),
  ].slice(0, _MAX_RECENTS);
  try {
    localStorage.setItem(_RECENTS_KEY, JSON.stringify(next));
  } catch {
    // localStorage may be unavailable (private mode) — non-fatal.
  }
  return next;
}

export function GlobalSearch({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [debounced, setDebounced] = useState("");
  const [recents, setRecents] = useState<string[]>(loadRecents);

  // Debounce the query → backend hit.
  useEffect(() => {
    const id = setTimeout(() => setDebounced(query.trim()), 250);
    return () => clearTimeout(id);
  }, [query]);

  // Reset the query each time the palette opens.
  useEffect(() => {
    if (open) {
      setQuery("");
      setDebounced("");
      setRecents(loadRecents());
    }
  }, [open]);

  const q = useQuery<GlobalSearchResponse>({
    queryKey: ["global-search", debounced],
    queryFn: () => api.globalSearch(debounced),
    enabled: open && debounced.length >= 1,
  });

  function go(link: string, searchTerm: string) {
    setRecents(saveRecent(searchTerm));
    onOpenChange(false);
    navigate(link);
  }

  if (!open) return null;

  const data = q.data;
  const hasResults =
    !!data && (data.groups.length > 0 || !!data.interpreted);

  return (
    <div
      className="fixed inset-0 z-[100] flex items-start justify-center bg-slate-900/40 p-4 pt-[12vh]"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onOpenChange(false);
      }}
    >
      <Command
        label={t("app.gs_aria")}
        shouldFilter={false}
        className="w-full max-w-xl overflow-hidden rounded-xl border border-border-subtle bg-white shadow-2xl"
      >
        <div className="flex items-center gap-2 border-b border-border-subtle px-4">
          <Search size={16} className="text-text-tertiary" aria-hidden />
          <Command.Input
            autoFocus
            value={query}
            onValueChange={setQuery}
            placeholder={t("app.gs_placeholder")}
            className="h-12 flex-1 bg-transparent text-sm text-text-primary outline-none placeholder:text-text-tertiary"
          />
          <kbd className="rounded border border-border-subtle px-1.5 py-0.5 text-[10px] text-text-tertiary">
            Esc
          </kbd>
        </div>

        <Command.List className="max-h-[60vh] overflow-y-auto p-2">
          {/* Empty query → recent searches */}
          {debounced.length === 0 && recents.length > 0 && (
            <Command.Group
              heading={t("app.gs_recent")}
              className="px-2 text-[11px] font-medium uppercase tracking-wider text-text-tertiary"
            >
              {recents.map((r) => (
                <Command.Item
                  key={r}
                  value={`recent-${r}`}
                  onSelect={() => setQuery(r)}
                  className="mt-1 flex cursor-pointer items-center gap-2 rounded-lg px-2 py-2 text-sm text-text-primary data-[selected=true]:bg-surface-muted"
                >
                  <Clock size={14} className="text-text-tertiary" />
                  {r}
                </Command.Item>
              ))}
            </Command.Group>
          )}

          {debounced.length === 0 && recents.length === 0 && (
            <p className="px-3 py-6 text-center text-sm text-text-tertiary">
              {t("app.gs_start_typing")}
            </p>
          )}

          {debounced.length >= 1 && q.isLoading && (
            <p className="px-3 py-6 text-center text-sm text-text-tertiary">
              {t("app.gs_searching")}
            </p>
          )}

          {debounced.length >= 1 && !q.isLoading && !hasResults && (
            <Command.Empty className="px-3 py-6 text-center text-sm text-text-tertiary">
              {t("app.gs_no_results", { q: debounced })}
            </Command.Empty>
          )}

          {/* AI-interpreted natural-language result */}
          {data?.interpreted && (
            <Command.Group
              heading={t("app.gs_suggested")}
              className="px-2 text-[11px] font-medium uppercase tracking-wider text-text-tertiary"
            >
              <Command.Item
                value="interpreted"
                onSelect={() =>
                  go(data.interpreted!.link, debounced)
                }
                className="mt-1 flex cursor-pointer items-center gap-2 rounded-lg border border-brand/30 bg-brand/5 px-2 py-2 text-sm data-[selected=true]:bg-brand/10"
              >
                <Sparkles size={14} className="text-brand" />
                <span className="flex-1 text-text-primary">
                  {data.interpreted.description}
                </span>
                <ArrowRight size={14} className="text-text-tertiary" />
              </Command.Item>
            </Command.Group>
          )}

          {/* M3X.UIPOLISH PR 29 — client-side Reports group from
              REPORTS_REGISTRY (instant; no server roundtrip). */}
          {debounced.length >= 1 &&
            (() => {
              const matches = searchReports(debounced).slice(0, 6);
              if (matches.length === 0) return null;
              return (
                <Command.Group
                  heading={t("app.gs_reports_group", "Reports")}
                  className="mt-2 px-2 text-[11px] font-medium uppercase tracking-wider text-text-tertiary"
                >
                  {matches.map((r) => (
                    <Command.Item
                      key={`report-${r.id}`}
                      value={`report-${r.id}`}
                      onSelect={() => go(r.to, debounced)}
                      className="mt-1 flex cursor-pointer items-center gap-2 rounded-lg px-2 py-2 text-sm data-[selected=true]:bg-surface-muted"
                    >
                      <Sparkles size={14} className="text-brand-teal" />
                      <span className="flex-1 text-text-primary">
                        {t(r.titleKey, r.id)}
                      </span>
                      <span className="text-xs text-text-tertiary">
                        {r.domain}
                      </span>
                    </Command.Item>
                  ))}
                </Command.Group>
              );
            })()}

          {/* M3X.UIPOLISH PR 30 — client-side Settings group from
              SETTINGS_REGISTRY (the 25-entry settings-hub index). */}
          {debounced.length >= 1 &&
            (() => {
              const matches = searchSettings(debounced).slice(0, 6);
              if (matches.length === 0) return null;
              return (
                <Command.Group
                  heading={t("app.gs_settings_group", "Settings")}
                  className="mt-2 px-2 text-[11px] font-medium uppercase tracking-wider text-text-tertiary"
                >
                  {matches.map((s) => (
                    <Command.Item
                      key={`setting-${s.id}`}
                      value={`setting-${s.id}`}
                      onSelect={() => go(s.to, debounced)}
                      className="mt-1 flex cursor-pointer items-center gap-2 rounded-lg px-2 py-2 text-sm data-[selected=true]:bg-surface-muted"
                    >
                      <Settings size={14} className="text-text-tertiary" />
                      <span className="flex-1 text-text-primary">
                        {t(s.titleKey, s.id)}
                      </span>
                      <span className="text-xs text-text-tertiary">
                        {s.section.replace(/_/g, " ")}
                      </span>
                    </Command.Item>
                  ))}
                </Command.Group>
              );
            })()}

          {/* Literal grouped results */}
          {data?.groups.map((group) => (
            <Command.Group
              key={group.kind}
              heading={group.label}
              className="mt-2 px-2 text-[11px] font-medium uppercase tracking-wider text-text-tertiary"
            >
              {group.items.map((item) => (
                <Command.Item
                  key={`${group.kind}-${item.id}`}
                  value={`${group.kind}-${item.id}`}
                  onSelect={() => go(item.link, debounced)}
                  className="mt-1 flex cursor-pointer items-center gap-2 rounded-lg px-2 py-2 text-sm data-[selected=true]:bg-surface-muted"
                >
                  <span className="flex-1 text-text-primary">
                    {item.title}
                  </span>
                  <span className="text-xs text-text-tertiary">
                    {item.subtitle}
                  </span>
                </Command.Item>
              ))}
            </Command.Group>
          ))}
        </Command.List>
      </Command>
    </div>
  );
}

/**
 * Hook: wires the ⌘K / Ctrl+K global shortcut to an open-state
 * setter. Mounted once by the AppShell.
 */
export function useGlobalSearchHotkey(
  onOpen: () => void,
): void {
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        onOpen();
      }
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onOpen]);
}
